import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, CheckCircle, XCircle, ArrowDownCircle, ArrowUpCircle, DollarSign } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { computeTWR } from '@/utils/twrCalculation';

interface PartnerAccount {
  id: string;
  user_id: string;
  current_value: number;
  total_invested: number;
  high_water_mark: number;
  performance_fee_pct: number;
  total_fees_paid: number;
  fee_history: unknown;
  performance_history: unknown;
  ytd_return_pct: number;
  display_name: string | null;
  display_email: string | null;
  capital_request_type: string | null;
  capital_request_amount: number | null;
  capital_request_status: string | null;
  capital_request_date: string | null;
  profiles: { full_name: string; email: string } | null;
}

interface CapitalRequestApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  partner: PartnerAccount | null;
  onComplete: () => void;
}

const CapitalRequestApprovalModal: React.FC<CapitalRequestApprovalModalProps> = ({
  isOpen,
  onClose,
  partner,
  onComplete,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!partner || partner.capital_request_status !== 'pending') return null;

  const isDeposit = partner.capital_request_type === 'deposit';
  const amount = partner.capital_request_amount || 0;
  const partnerName = partner.display_name || partner.profiles?.full_name || 'Unknown';

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      const newTotalInvested = isDeposit
        ? partner.total_invested + amount
        : partner.total_invested - amount;

      const newCurrentValue = isDeposit
        ? partner.current_value + amount
        : partner.current_value - amount;

      // For deposits, the new capital hasn't earned anything yet, so HWM rises by the deposit
      // to prevent the new capital from being charged a performance fee until it generates profit.
      // For withdrawals, reduce HWM proportionally to preserve the fee fairness.
      let newHWM: number;
      if (isDeposit) {
        newHWM = partner.high_water_mark + amount;
      } else {
        // Proportional HWM reduction: HWM * (new_value / old_value)
        const ratio = partner.current_value > 0 ? newCurrentValue / partner.current_value : 1;
        newHWM = partner.high_water_mark * ratio;
      }

      // Add a performance history entry for the capital event
      const existingHistory = Array.isArray(partner.performance_history)
        ? partner.performance_history
        : [];
      const updatedHistory = [
        ...existingHistory,
        {
          date: new Date().toISOString().split('T')[0],
          value: newCurrentValue,
          change_pct: 0, // Capital event, not a return
          hwm: newHWM,
          event: isDeposit ? 'deposit' : 'withdrawal',
          event_amount: amount,
        },
      ];

      const ytdReturnPct = computeTWR(
        updatedHistory as Array<{ date: string; value: number; event?: string; event_amount?: number }>,
        newCurrentValue,
        newTotalInvested
      );

      const { error } = await supabase
        .from('partner_accounts')
        .update({
          current_value: newCurrentValue,
          total_invested: newTotalInvested,
          high_water_mark: newHWM,
          performance_history: updatedHistory,
          ytd_return_pct: ytdReturnPct,
          capital_request_status: 'approved',
          last_updated: new Date().toISOString(),
        })
        .eq('id', partner.id);

      if (error) throw error;

      toast({
        title: `${isDeposit ? 'Deposit' : 'Withdrawal'} Approved`,
        description: `$${amount.toLocaleString()} ${isDeposit ? 'deposit' : 'withdrawal'} for ${partnerName} has been processed. Account updated.`,
      });

      onComplete();
      onClose();
    } catch (error: any) {
      console.error('Error approving capital request:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to process the capital request.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeny = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('partner_accounts')
        .update({
          capital_request_status: 'denied',
          last_updated: new Date().toISOString(),
        })
        .eq('id', partner.id);

      if (error) throw error;

      toast({
        title: 'Request Denied',
        description: `${isDeposit ? 'Deposit' : 'Withdrawal'} request from ${partnerName} has been denied.`,
      });

      onComplete();
      onClose();
    } catch (error: any) {
      console.error('Error denying capital request:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to deny the request.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const newValue = isDeposit ? partner.current_value + amount : partner.current_value - amount;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-delphi-dark border-delphi-gray/30">
        <DialogHeader>
          <DialogTitle className="text-xl text-delphi-light flex items-center gap-2">
            {isDeposit ? (
              <ArrowDownCircle size={20} className="text-green-500" />
            ) : (
              <ArrowUpCircle size={20} className="text-red-500" />
            )}
            {isDeposit ? 'Deposit' : 'Withdrawal'} Request
          </DialogTitle>
          <DialogDescription className="text-delphi-light/60">
            Review and process this capital request from {partnerName}.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Request Details */}
          <div className="bg-delphi-dark/50 border border-delphi-gray/20 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-delphi-light/60 text-sm">Partner</span>
              <span className="text-delphi-light font-medium">{partnerName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-delphi-light/60 text-sm">Request Type</span>
              <span className={`font-medium ${isDeposit ? 'text-green-400' : 'text-red-400'}`}>
                {isDeposit ? 'Deposit' : 'Withdrawal'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-delphi-light/60 text-sm">Amount</span>
              <span className="text-delphi-light font-bold text-lg">${amount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-delphi-light/60 text-sm">Requested</span>
              <span className="text-delphi-light/70 text-sm">
                {partner.capital_request_date
                  ? new Date(partner.capital_request_date).toLocaleDateString()
                  : 'N/A'}
              </span>
            </div>
          </div>

          {/* Impact Summary */}
          <div className="bg-delphi-gray/10 border border-delphi-gray/20 rounded-lg p-4 space-y-2">
            <h4 className="text-sm font-medium text-delphi-light flex items-center gap-2 mb-3">
              <DollarSign size={14} className="text-delphi-red" />
              Account Impact
            </h4>
            <div className="flex justify-between text-sm">
              <span className="text-delphi-light/60">Current Value</span>
              <span className="text-delphi-light">${partner.current_value.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-delphi-light/60">{isDeposit ? '+' : '−'} {isDeposit ? 'Deposit' : 'Withdrawal'}</span>
              <span className={isDeposit ? 'text-green-400' : 'text-red-400'}>
                ${amount.toLocaleString()}
              </span>
            </div>
            <div className="border-t border-delphi-gray/20 pt-2 flex justify-between text-sm font-medium">
              <span className="text-delphi-light/80">New Value</span>
              <span className="text-delphi-light">${newValue.toLocaleString()}</span>
            </div>
          </div>

          {/* HWM Note */}
          <p className="text-xs text-delphi-light/50">
            {isDeposit
              ? 'The high-water mark will increase by the deposit amount so new capital is not subject to performance fees until it generates profit.'
              : 'The high-water mark will be proportionally reduced to maintain fair fee calculations.'}
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleDeny}
            className="text-red-400 border-red-400/30 hover:bg-red-400/10"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <XCircle className="w-4 h-4 mr-2" />
            )}
            Deny
          </Button>
          <Button
            onClick={handleApprove}
            className={isDeposit ? 'bg-green-600 hover:bg-green-700' : 'bg-delphi-red hover:bg-delphi-red/80'}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Approve {isDeposit ? 'Deposit' : 'Withdrawal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CapitalRequestApprovalModal;
